<template>
  <div class="line-adjustments">
    <div v-for="adj in localAdjustments" :key="adj._localId" class="adjustment-row d-flex align-items-start flex-wrap gap-2 py-1">
      <div style="width: 250px; flex-shrink: 0;">
        <wiskSelect
          :modelValue="adjTypeWithTitle(adj.adjustment_type_id)"
          :items="allAdjustmentTypes"
          :label="translations.txtAdjustmentTypeSelectorLabel"
          trackBy="id"
          disabled
        />
      </div>

      <div style="width: 140px; flex-shrink: 0;">
        <wiskInput
          :modelValue="adj.rate"
          :prefix="ratePrefix(adj.adjustment_type_id)"
          :suffix="rateSuffix(adj.adjustment_type_id)"
          inputType="number" :decimals="4" decimalsAsNeeded :minDecimals="2"
          :disabled="disabled"
          @update:modelValue="adj.rate = $event; adj._dirty = true"
          :label="translations.txtAdjustmentRate"
          class="mb-0"
        />
        <div v-if="basisLabel(adj.adjustment_type_id)" class="text-gray-400">{{ basisLabel(adj.adjustment_type_id) }}</div>
      </div>

      <div v-if="!hideTotals" style="width: 120px; flex-shrink: 0;">
        <wiskInput v-if="adj._dirty"
          modelValue="—"
          inputType="text"
          inputClass="text-end"
          disabled
          :label="translations.txtGenericTotal"
        />
        <wiskInput v-else
          :modelValue="adj.computed_total"
          :prefix="currency"
          inputType="number" :decimals="2" :minDecimals="2"
          disabled
          :label="translations.txtGenericTotal"
        />
      </div>

      <div class="d-flex align-items-center gap-2 flex-shrink-0">
        <AdjustmentTypeCapitalizeBadge :capitalizeIntoCost="adj.capitalize_into_cost" />

        <b-button v-if="!disabled" size="sm" variant="link" @click="removeAdjustment(adj)" v-tooltip="translations.txtGenericRemove">
          <icon name="wisk-trash" :scale="0.9" class="text-danger" />
        </b-button>
      </div>
    </div>

    <div v-if="showAddForm" class="d-flex align-items-center gap-2 py-2">
      <div style="min-width: 180px; max-width: 250px; flex: 1 1 180px; position: relative;">
        <wiskSelect
          :items="activeAdjustmentTypes"
          :modelValue="null"
          @change="onNewTypeSelected"
          :label="translations.txtAdjustmentTypeSelectorLabel"
          :addNewItem="addAdjustmentTypeConfig"
          trackBy="id"
          fixedWrapper
        />
        <b-button class="text-primary" style="position: absolute; top: -25px; right: -5px;" variant="link" v-tooltip="translations.txtAdjustmentTypes"
          @click="setGlobalAction({ type: 'adjustmentTypes', action: {} })">
          <icon name="wisk-options" :scale="0.9" />
        </b-button>
      </div>
      <b-button size="sm" variant="link" @click="showAddForm = false" v-tooltip="translations.txtGenericCancel" class="flex-shrink-0 align-self-start mt-1">
        <icon name="wisk-trash" :scale="0.9" class="text-danger" />
      </b-button>
    </div>

    <b-button v-if="!disabled && !showAddForm" size="sm" variant="link" @click="showAddForm = true" class="mt-1 ps-0 text-primary">
      <icon name="wisk-plus" :scale="0.7" class="me-1" />
      {{ translations.txtAdjustmentAddAdjustment }}
    </b-button>

    <div v-if="!hideTotals" class="mt-3 pt-2 border-top">
      <div class="d-flex justify-content-between py-1">
        <span class="text-muted">{{ translations.txtGenericSubTotal }}</span>
        <span>{{ currencyFormat(getSubtotal(lineItem)) }}</span>
      </div>
      <div v-for="adj in localAdjustments" :key="'total-' + adj._localId" class="d-flex justify-content-between py-1 small text-muted">
        <span>{{ adjTypeLabel(adj.adjustment_type_id) }}</span>
        <span>{{ adj._dirty ? '—' : currencyFormat(adj.computed_total || 0) }}</span>
      </div>
      <div class="d-flex justify-content-between py-1 fw-bold">
        <span>{{ translations.txtAdjustmentEffectiveTotal }}</span>
        <span>{{ anyDirty ? '—' : currencyFormat(getEffectiveTotal(lineItem)) }}</span>
      </div>
    </div>

    <div v-if="!disabled && !hideSave" class="mt-3 d-flex justify-content-end">
      <b-button variant="primary" @click="onSave" :disabled="saving || !anyDirty">
        {{ translations.txtGenericSave }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { currencyFormat } from '@/modules/utils'
import AdjustmentTypeCapitalizeBadge from '@/components/accounting/AdjustmentTypeCapitalizeBadge'

let localIdCounter = 0

export default {
  name: 'LineAdjustmentsEditor',
  components: { AdjustmentTypeCapitalizeBadge },
  emits: ['saveAdjustments', 'update:adjustments'],
  props: {
    lineItem: Object,
    disabled: Boolean,
    saving: Boolean,
    hideSave: Boolean,
    hideTotals: Boolean,
    getSubtotal: { type: Function, default: item => item?.subtotal ?? 0 },
    getEffectiveTotal: { type: Function, default: item => item?.effective_total ?? 0 }
  },
  data() {
    return {
      localAdjustments: [],
      showAddForm: false,
      hasDeletedExisting: false
    }
  },
  computed: {
    ...mapState(['translations', 'adjustmentTypes', 'adjustmentTypesById']),
    ...mapGetters(['currency']),
    allAdjustmentTypes() {
      return this.adjustmentTypes.map(t => ({ ...t, title: t.label }))
    },
    activeAdjustmentTypes() {
      return this.allAdjustmentTypes.filter(t => !t.archived)
    },
    anyDirty() {
      return this.hasDeletedExisting || this.localAdjustments.some(a => a._dirty)
    },
    basisLabels() {
      return {
        per_unit: this.translations.txtAdjustmentTypeBasisPerUnit,
        per_case: this.translations.txtAdjustmentTypeBasisPerCase,
        percent_of_line: this.translations.txtAdjustmentTypeBasisPercentOfLine
      }
    },
    addAdjustmentTypeConfig() {
      return {
        action: (id, searchQuery, value, callbackItemInjected) => {
          this.setGlobalAction({
            type: 'adjustmentTypeEdit',
            action: {
              id: 0,
              onChange: type => {
                callbackItemInjected(type)
              }
            }
          })
        },
        label: this.translations.txtAdjustmentTypeNew
      }
    }
  },
  methods: {
    currencyFormat,
    ...mapActions(['setGlobalAction']),
    adjTypeWithTitle(typeId) {
      const type = this.adjustmentTypesById[typeId]
      return type ? { ...type, title: type.label } : null
    },
    adjTypeLabel(typeId) {
      const type = this.adjustmentTypesById[typeId]
      return type?.label || ''
    },
    ratePrefix(typeId) {
      const type = this.adjustmentTypesById[typeId]
      return type?.basis === 'percent_of_line' ? '' : this.currency
    },
    rateSuffix(typeId) {
      const type = this.adjustmentTypesById[typeId]
      return type?.basis === 'percent_of_line' ? '%' : ''
    },
    basisLabel(typeId) {
      const type = this.adjustmentTypesById[typeId]
      return this.basisLabels[type?.basis] ?? null
    },
    removeAdjustment(adj) {
      if (adj.id) {
        this.hasDeletedExisting = true
      }
      this.localAdjustments = this.localAdjustments.filter(a => a._localId !== adj._localId)
    },
    onNewTypeSelected(type) {
      if (!type) return
      this.showAddForm = false
      this.localAdjustments.push({
        _localId: ++localIdCounter,
        _dirty: true,
        id: 0,
        adjustment_type_id: type.id,
        rate: type.default_rate || 0,
        computed_total: 0,
        capitalize_into_cost: type.capitalize_into_cost || false
      })
    },
    onSave() {
      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      const adjustments = this.localAdjustments.map(({ _localId, _dirty, ...adj }) => ({ ...adj }))
      this.$emit('saveAdjustments', adjustments)
    }
  },
  watch: {
    'lineItem.adjustments': {
      immediate: true,
      handler(adjustments) {
        this.localAdjustments = (adjustments || []).map(a => ({ ...a, _localId: ++localIdCounter, _dirty: false }))
        this.hasDeletedExisting = false
      }
    },
    localAdjustments: {
      deep: true,
      handler(adjustments) {
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        this.$emit('update:adjustments', adjustments.map(({ _localId, _dirty, ...adj }) => ({ ...adj })))
      }
    }
  }
}
</script>
